import { Delete } from '@mui/icons-material';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { EEntityType, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import type { ISmallRole } from '@sharedInterfaces/IWhoIAm';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import Item from '@src/App/Components/Items/EntityItems/Item/Item';
import { NewBadge } from '@src/App/Components/Items/ItemBadge/ItemBadge';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

import RoleSearchBox from '../../searchBoxes/RoleSearchBox/RoleSearchBox';

interface IRoleInputRole {
    id: number;
    title: string;
}
interface RolesInputProps<T extends IRoleInputRole> {
    allRoles: ISmallRole[];
    selectedRoles: T[];
    showMode: boolean;
    size: 'medium' | 'small';
    disabled?: boolean;
    helperText?: string;
    onChange(val: T[]): void;
}

/**
 * Represents an input component for roles.
 *
 * @param {RolesInputProps} allRoles - A list of all roles.
 * @param {RolesInputProps} selectedRoles - A list of selected roles.
 * @param {RolesInputProps} showMode - The mode to display the roles.
 * @param {RolesInputProps} size - The size of the input.
 * @param {RolesInputProps} onChange - Event handler for when the input value changes.
 *
 * @returns {JSX.Element} The rendered input component.
 */
function RolesInput<T extends IRoleInputRole>({
    allRoles,
    selectedRoles,
    showMode,
    size,
    disabled,
    helperText,
    onChange,
}: RolesInputProps<T>) {
    if (!allRoles) return <div />;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).roles;

    const filteredAllRoles = allRoles.filter(
        (a) => selectedRoles.find((s) => s.id === a.id || s.title === a.title) === undefined,
    ); //TO hide allready selected roles

    const rolesDOM = selectedRoles.map((role) => {
        const withoutLink = !showMode || role.id === undefined;
        return (
            <Item
                key={role.title + role.id}
                rightContent={
                    !showMode &&
                    !disabled && (
                        <IconButton
                            title={langStrings.oftenUsed.delete}
                            size="small"
                            onClick={(): void => {
                                const newSelectedRoles = selectedRoles
                                    .map((e) => Object.assign({}, e))
                                    .filter((e) => e.id !== role.id)
                                    .sort((a, b) => a.title.localeCompare(b.title));
                                onChange(newSelectedRoles);
                            }}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    )
                }
            >
                {withoutLink ? (
                    <>
                        {role.title}
                        {!role.id && <NewBadge />}
                    </>
                ) : (
                    <NavLink key={role.id} to={entityTypeToLink(role.id, EEntityType.ROLE)} className="role">
                        {role.title}
                    </NavLink>
                )}
            </Item>
        );
    });
    return (
        <Stack spacing={2} sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
            {selectedRoles.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedRoles.map((role) => {
                        const withoutLink = !showMode || role.id === undefined;
                        return (
                            <Chip
                                key={role.title + role.id}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {withoutLink ? (
                                            <>
                                                {role.title}
                                                {!role.id && <NewBadge />}
                                            </>
                                        ) : (
                                            <NavLink
                                                key={role.id}
                                                to={entityTypeToLink(role.id, EEntityType.ROLE)}
                                                style={{ textDecoration: 'none', color: 'inherit' }}
                                            >
                                                {role.title}
                                            </NavLink>
                                        )}
                                    </Box>
                                }
                                onDelete={
                                    !showMode && !disabled
                                        ? () => {
                                              const newSelectedRoles = selectedRoles
                                                  .map((e) => Object.assign({}, e))
                                                  .filter((e) => e.id !== role.id)
                                                  .sort((a, b) => a.title.localeCompare(b.title));
                                              onChange(newSelectedRoles);
                                          }
                                        : undefined
                                }
                                variant="outlined"
                                size={size === 'small' ? 'small' : 'medium'}
                                sx={{
                                    borderRadius: 2,
                                    '& .MuiChip-deleteIcon': {
                                        fontSize: '1rem',
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
            )}
            {!showMode && !disabled && (
                <Box>
                    <RoleSearchBox
                        allRoles={filteredAllRoles}
                        onSelectedRole={(selectedRole) => {
                            const newSelectedRoles = selectedRoles.map((e) => Object.assign({}, e));
                            newSelectedRoles.push(selectedRole as unknown as T);
                            newSelectedRoles.sort((a, b) => a.title.localeCompare(b.title));
                            onChange(newSelectedRoles);
                        }}
                    />
                </Box>
            )}
            {helperText && (
                <Typography variant="caption" color="textSecondary">
                    {helperText}
                </Typography>
            )}
        </Stack>
    );
}

export default RolesInput;
